import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiTextTwist } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';

const SHARE_URL = 'https://wordzingo.com/text-twist-solver';

const MIN_OPTIONS = [2, 3, 4, 5, 6, 7];

// Distinct accent palette so each word-length group reads as its own block
const GROUP_PALETTE = [
  { accent: '#4f46e5', border: '#c7d2fe', bg: '#eef2ff', text: '#3730a3' }, // indigo
  { accent: '#0891b2', border: '#a5f3fc', bg: '#ecfeff', text: '#155e75' }, // cyan
  { accent: '#16a34a', border: '#bbf7d0', bg: '#f0fdf4', text: '#166534' }, // green
  { accent: '#ea580c', border: '#fed7aa', bg: '#fff7ed', text: '#9a3412' }, // orange
  { accent: '#db2777', border: '#fbcfe8', bg: '#fdf2f8', text: '#9d174d' }, // pink
  { accent: '#7c3aed', border: '#ddd6fe', bg: '#f5f3ff', text: '#5b21b6' }, // purple
  { accent: '#b45309', border: '#fde68a', bg: '#fffbeb', text: '#92400e' }, // amber
];

export default function TextTwistSolver() {
  const [letters, setLetters] = useState('');
  const [minLen, setMinLen]   = useState(3);
  const [data, setData]       = useState(null);   // { groups, count, max_length, letters }
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [copied, setCopied]   = useState(false);
  const [showShare, setShowShare] = useState(false);

  async function handleSolve(e) {
    e.preventDefault();
    const clean = letters.replace(/[^a-zA-Z]/g, '');
    if (clean.length < 3) { setError('Enter at least 3 letters'); return; }
    setLoading(true); setError(''); setData(null); setCopied(false);
    try {
      const res = await apiTextTwist(clean, minLen);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  }

  function copyAll() {
    if (!data) return;
    const text = data.groups
      .map(g => `${g.length}-letter words (${g.count}):\n${g.words.map(w => w.toUpperCase()).join(', ')}`)
      .join('\n\n');
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  function clearAll() {
    setLetters('');
    setData(null);
    setError('');
    setCopied(false);
  }

  const hasResults = data && data.count > 0;

  // Short, shareable summary of the solve (top words from the longest group)
  const shareText = hasResults ? [
    'Text Twist Solver 🔡',
    `Letters: ${data.letters.toUpperCase()}`,
    `Found ${data.count} word${data.count === 1 ? '' : 's'} — longest is ${data.max_length} letters`,
    `Top: ${data.groups[0].words.slice(0, 6).map(w => w.toUpperCase()).join(', ')}`,
    '',
    `Solve yours free at ${SHARE_URL}`,
  ].join('\n') : '';

  return (
    <div className="game-page">
      <PageSEO page="text-twist-solver" />
      <div className="container" style={{ maxWidth: 720 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔡</span>
          <h1>Text Twist Solver</h1>
          <p>Enter your Text Twist letters and instantly see every word — sorted by length, longest first.</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Enter Your Letters</label>
              <input
                className="big-input"
                value={letters}
                onChange={e => setLetters(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 12))}
                placeholder="e.g. TWISTR"
                maxLength={12}
                autoFocus
                autoCapitalize="characters"
                spellCheck={false}
              />
              <small className="text-muted">
                {letters.replace(/[^a-zA-Z]/g, '').length} letters · type the 6 or 7 letters from your Text Twist round
              </small>
            </div>

            <div className="mb-3">
              <label className="fw-bold mb-1">Minimum Word Length</label>
              <div className="d-flex flex-wrap gap-2">
                {MIN_OPTIONS.map(n => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setMinLen(n)}
                    className="btn"
                    style={{
                      borderRadius: 50, fontWeight: 700, padding: '0.35rem 1rem',
                      border: '2px solid var(--primary)',
                      background: minLen === n ? 'var(--primary)' : '#fff',
                      color: minLen === n ? '#fff' : 'var(--primary)',
                    }}
                  >
                    {n}+
                  </button>
                ))}
              </div>
              <small className="text-muted">Text Twist accepts 3-letter words and up — leave this at 3 to match the game.</small>
            </div>

            <div className="d-flex flex-wrap gap-2 align-items-center">
              <button type="submit" className="solve-btn" disabled={loading || letters.replace(/[^a-zA-Z]/g, '').length < 3}>
                {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Solving…</> : '🔡 Solve Text Twist'}
              </button>
              <button type="button" onClick={clearAll} disabled={!letters && data === null}
                style={{
                  border: '2px solid #e2e8f0', borderRadius: 12, background: '#fff',
                  padding: '0.7rem 1.4rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif',
                  fontSize: '1rem', color: '#64748b',
                  cursor: (!letters && data === null) ? 'not-allowed' : 'pointer',
                  opacity: (!letters && data === null) ? 0.5 : 1,
                }}>
                ✕ Clear
              </button>
            </div>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {data !== null && (
          <div className="solver-card">
            {hasResults ? (
              <>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <p className="result-count mb-0">
                    Found <strong>{data.count}</strong> word{data.count === 1 ? '' : 's'} from{' '}
                    <strong>{data.letters.toUpperCase()}</strong>
                  </p>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm" onClick={copyAll}
                      style={{ borderRadius: 50, fontWeight: 700, border: '2px solid var(--primary)', color: 'var(--primary)', background: '#fff' }}>
                      {copied ? '✓ Copied!' : '📋 Copy All'}
                    </button>
                    <button className="btn btn-sm" onClick={() => setShowShare(true)}
                      style={{ borderRadius: 50, fontWeight: 700, border: 'none', color: '#fff', background: 'var(--primary)' }}>
                      📤 Share
                    </button>
                  </div>
                </div>

                {data.groups.map((g, idx) => {
                  const c = GROUP_PALETTE[idx % GROUP_PALETTE.length];
                  const usesAll = g.length === data.letters.length;
                  return (
                    <div key={g.length} style={{
                      border: `1px solid ${c.border}`,
                      borderLeft: `6px solid ${c.accent}`,
                      background: c.bg,
                      borderRadius: 14,
                      padding: '0.85rem 1rem 1rem',
                      marginBottom: '1.1rem',
                    }}>
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem', marginBottom: '0.7rem' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                          background: c.accent, color: '#fff', fontWeight: 800, fontSize: '1.05rem',
                        }}>{g.length}</span>
                        <span style={{ fontWeight: 800, color: c.accent, fontSize: '1.02rem' }}>
                          {g.length}-letter words
                        </span>
                        <span style={{
                          background: '#fff', border: `1.5px solid ${c.border}`, color: c.text,
                          borderRadius: 50, padding: '0.1rem 0.65rem', fontWeight: 800, fontSize: '0.8rem',
                        }}>{g.count}</span>
                        {usesAll && (
                          <span className="badge" style={{ background: '#db2777', color: '#fff', fontSize: '0.72rem' }}>
                            Uses all letters 🏆
                          </span>
                        )}
                        {g.length === data.max_length && !usesAll && (
                          <span className="badge" style={{ background: '#16a34a', color: '#fff', fontSize: '0.72rem' }}>Longest</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem' }}>
                        {g.words.map(w => (
                          <DefinableWord key={w} word={w} style={{
                            display: 'inline-block',
                            background: '#fff',
                            border: `1.5px solid ${c.accent}`,
                            color: c.text,
                            borderRadius: 8,
                            padding: '0.32rem 0.62rem',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            letterSpacing: '0.05em',
                            textDecoration: 'none',
                          }}>
                            {w.toUpperCase()}
                          </DefinableWord>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <p className="text-muted text-center mb-0">
                No words found for those letters. Double-check the spelling, or try lowering the minimum word length.
              </p>
            )}
          </div>
        )}

        {showShare && (
          <ShareModal
            shareText={shareText}
            gameUrl={SHARE_URL}
            onClose={() => setShowShare(false)}
          />
        )}

        <SeoContent game="text-twist-solver" />
      </div>
    </div>
  );
}
